import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  TrackingStatus,
  getTrackingStatusTranslation,
  compareTrackingStatusOrder,
  SupportedLocale,
  SUPPORTED_LOCALES,
} from "@babackapp/tracking-status-registry";

export interface TrackingStatusOption {
  value: TrackingStatus;
  label: string;
  description: string;
  customerDescription: string;
}

const EXCLUDED_STATUSES: TrackingStatus[] = [
  TrackingStatus.ORDER_NOT_FULFILLED,
];

const TRIGGER_TRACKING_STATUSES = Object.values(TrackingStatus)
  .filter((status) => !EXCLUDED_STATUSES.includes(status))
  .sort(compareTrackingStatusOrder) as Exclude<
  TrackingStatus,
  "ORDER_NOT_FULFILLED"
>[];

function toSupportedLocale(locale: string): SupportedLocale {
  const normalizedLocale = locale.split("-")[0]?.toLowerCase() ?? "en";
  if (SUPPORTED_LOCALES.includes(normalizedLocale as SupportedLocale)) {
    return normalizedLocale as SupportedLocale;
  }
  return "en";
}

export function useTrackingStatusList(): TrackingStatusOption[] {
  const { i18n } = useTranslation("trackoffice");
  const language = i18n.language ?? "en";
  const locale = toSupportedLocale(language);

  return useMemo(() => {
    return TRIGGER_TRACKING_STATUSES.map((status) => {
      const translation = getTrackingStatusTranslation(status, locale);
      return {
        value: status,
        label: translation.internalTitle,
        description: translation.internalDescription,
        customerDescription: translation.description,
      };
    });
  }, [locale]);
}

export function useTrackingStatusMap(): Record<
  Exclude<TrackingStatus, "ORDER_NOT_FULFILLED">,
  TrackingStatusOption
> {
  const list = useTrackingStatusList();

  return useMemo(() => {
    return list.reduce(
      (acc, item) => {
        acc[item.value as Exclude<TrackingStatus, "ORDER_NOT_FULFILLED">] = item;
        return acc;
      },
      {} as Record<Exclude<TrackingStatus, "ORDER_NOT_FULFILLED">, TrackingStatusOption>
    );
  }, [list]);
}
